import { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'warning';
type Size = 'sm' | 'md';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
}

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-accent text-dash-bg hover:bg-cyan-300 disabled:bg-dash-dim disabled:text-dash-muted',
  secondary:
    'bg-dash-surface border border-dash-border text-dash-primary hover:border-dash-muted disabled:opacity-50',
  ghost:
    'bg-transparent text-dash-secondary hover:text-dash-primary hover:bg-dash-surface disabled:opacity-50',
  danger:
    'bg-error/20 text-error border border-error/40 hover:bg-error/30 disabled:opacity-50',
  warning:
    'bg-warning/15 text-warning border border-warning/40 hover:bg-warning/25 disabled:opacity-50',
};

const sizeClasses: Record<Size, string> = {
  sm: 'px-2.5 py-1 text-xs',
  md: 'px-3.5 py-2 text-sm',
};

// whitespace-nowrap keeps a button's label on one line even when a cramped
// flex row would otherwise squeeze it (flex items with wrappable text shrink
// below their content). Combined with the default min-width:auto this stops
// the label from wrapping to two lines; genuinely tight rows should wrap as
// whole buttons via flex-wrap on the container, not by breaking the label.
const BASE =
  'inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-colors disabled:cursor-not-allowed active:scale-[0.98] transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:ring-offset-2 focus-visible:ring-offset-dash-bg';

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  children,
  ...props
}: ButtonProps) {
  const cls = `${BASE} ${variantClasses[variant]} ${sizeClasses[size]} ${className ?? ''}`;
  return (
    <button className={cls} {...props}>
      {children}
    </button>
  );
}
